import { z } from 'zod';

import { playerSchema } from '../roster';
import { Thresholds } from '../settings';
import { baseEventSchema } from './base-event';
import { ContestingPose, ShotContention } from './shot-contention';
import { ShotLoad } from './shot-load';
import { ShotOrigin } from './shot-origin';
import { ShotOriginArea } from './shot-origin-area';
import { ShotResult } from './shot-result';
import { ShotTypeTactical } from './shot-type-tactical';

export type ShotEvent = z.output<typeof shotEventSchema>;
export type ShotEventDTO = z.input<typeof shotEventSchema>;

export const shotEventSchema = baseEventSchema.extend({
  contestingArea: z.number(),
  contestingPose: z.enum(ContestingPose),
  shotLoad: z.number(),
  shotOrigin: z.enum(ShotOrigin),
  shotOriginArea: z.enum(ShotOriginArea),
  shotResult: z.enum(ShotResult),
  shotTypeTactical: z.enum(ShotTypeTactical).catch(ShotTypeTactical.UNKNOWN),
  type: z.literal('shot'),
});

export type ShotEventWithPlayer = z.output<typeof shotEventWithPlayerSchema>;
export type ShotEventWithPlayerDTO = z.input<typeof shotEventWithPlayerSchema>;

export const shotEventWithPlayerSchema = shotEventSchema.extend({
  player: playerSchema,
});

export const DEFAULT_SHOT_LOAD_LOW = 2;
export const DEFAULT_SHOT_LOAD_HIGH = 3.5;

export const ShotEventUtils = {
  getContention(shot: ShotEvent): ShotContention {
    if (ShotEventUtils.isContested(shot)) {
      return ShotContention.CONTESTED;
    }

    return ShotContention.UNCONTESTED;
  },
  getShotLoad(shotEvent: ShotEvent, threholds: Thresholds): ShotLoad {
    if (shotEvent.shotLoad < threholds.shotLoadLow) {
      return ShotLoad.LOW;
    }
    if (shotEvent.shotLoad >= threholds.shotLoadHigh) {
      return ShotLoad.HIGH;
    }

    return ShotLoad.MEDIUM;
  },
  isContested(shotEvent: ShotEvent): boolean {
    const highlyContestedAreas = [1, 2, 3, 10, 11, 12, 13, 14];
    const lightlyContestedAreas = [14, 15, 22, 23, 25, 36, 37, 48];

    const { contestingArea, contestingPose } = shotEvent;

    // Shots in highly contested areas are considered contested regardless of pose
    if (highlyContestedAreas.includes(contestingArea)) {
      return true;
    }

    // Shots in lightly contested areas are considered contested only if the pose is hands up
    if (
      lightlyContestedAreas.includes(contestingArea) &&
      contestingPose === ContestingPose.HANDS_UP
    ) {
      return true;
    }

    return false;
  },
  isMake(shotEvent: ShotEvent): boolean {
    return shotEvent.shotResult === ShotResult.MAKE;
  },
  isMiss(shotEvent: ShotEvent): boolean {
    return shotEvent.shotResult === ShotResult.MISS;
  },
  isLowIntensity({ shotLoad }: ShotEvent, threshold = DEFAULT_SHOT_LOAD_LOW): boolean {
    return shotLoad < threshold;
  },
  isHighIntensity({ shotLoad }: ShotEvent, threshold = DEFAULT_SHOT_LOAD_HIGH): boolean {
    return shotLoad >= threshold;
  },
  isMediumIntensity(shotEvent: ShotEvent, thresholds: Thresholds | undefined | null): boolean {
    return (
      !ShotEventUtils.isLowIntensity(shotEvent, thresholds?.shotLoadLow) &&
      !ShotEventUtils.isHighIntensity(shotEvent, thresholds?.shotLoadHigh)
    );
  },
};
